import classNames from "classnames";
import { Button } from "PFComponents/button";
import { Inline, Stack } from "PFComponents/containers";
import { Tile } from "PFComponents/tile";
import { Typography } from "PFComponents/typography";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./changelog_item.module.scss";

const MAX_HEIGHT = 200;

type ChangelogItemProps = {
  before?: ReactNode;
  after?: ReactNode;
};

type BlockProps = {
  label: string;
  content: ReactNode;
};

const Block = ({ label, content }: BlockProps) => (
  <Stack className={css.block}>
    <Typography variant="labelBold">{label}</Typography>
    <Typography variant="labelRegular" tag="span">
      {content}
    </Typography>
  </Stack>
);

export const ChangelogItem = ({ before, after }: ChangelogItemProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "notificationChangelog" });
  const { t: tCore } = useTranslation("core");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) {
      return;
    }

    const checkOverflow = () => setIsOverflowing(contentElement.scrollHeight > MAX_HEIGHT);
    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(contentElement);

    return () => resizeObserver.disconnect();
  }, [before, after]);

  const handleMoreLessClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  return (
    <Tile paddingVariant="md" border className={classNames({ [css.overflowing]: isOverflowing })}>
      <div ref={contentRef} className={css.content} style={{ maxHeight: !isExpanded ? MAX_HEIGHT : "none" }}>
        <Inline wrap="wrap">
          {before && <Block label={t("before")} content={before} />}
          {after && <Block label={t("after")} content={after} />}
        </Inline>
      </div>
      {isOverflowing && !isExpanded && <div className={css.gradient} />}
      {isOverflowing && (
        <Button
          small
          kind="text"
          onClick={handleMoreLessClick}
          onKeyDown={(event) => event.stopPropagation()}
          className={css.toggleButton}
        >
          {isExpanded ? tCore("showLess") : tCore("showMore")}
        </Button>
      )}
    </Tile>
  );
};
